import { createTheme, GlobalStyles } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import axios from "axios";
import * as React from "react";
import cookie from "react-cookies";
import { Config } from "../../config/config";
import { Authorization } from "../../containers/Authorization";
import { LayoutWrapper } from "../../containers/LayoutWrapper";
import { Helpers, setGlobalVar } from "../../utils/helpers";
import { FatalError } from "../Error/Fatal";
import { LoadingIndicator } from "../LoadingIndicator";
import { heartBeat } from "../SessionCheck";
import { SessionUser } from "../User/User";
import DarkMode from "./themes/dark";
import LightMode from "./themes/light";

interface ApplicationState {
    isAuthorized: boolean;
    // This is check before loading everything
    initHeartbeat: boolean;
    activeUser: any;
    error: any;
    configLoading: boolean;
    showLoginPopup: boolean;
    mode: string;
}

export class App extends React.Component<{}, ApplicationState> {
    sessionCheckTimer: any;

    constructor(props) {
        super(props);

        this.state = {
            isAuthorized: false,
            activeUser: null,
            initHeartbeat: false,
            error: null,
            configLoading: true,
            showLoginPopup: false,
            mode: "dark"
        };
    }
    faviconUpdate = () => {
        const favicon = document.getElementById("favicon") as any;

        if (favicon) {
            favicon.href = require(`../../../assets/images/icons/${Config.getInstance.getSettingByKey("SITE_LOGO")}`).default;
        }
    }

    requestHeaders = () => ({
        headers: { Authorization: Helpers.getSessionId() },
    })

    fetchShopPackages = () => axios.get(`${Config.getInstance.apiUrl}/api/shop_packages`, this.requestHeaders());

    fetchItems = () => axios.get(`${Config.getInstance.apiUrl}/api/items`, this.requestHeaders());

    fetchLocalization = () => axios.get(`${Config.getInstance.apiUrl}/api/localization`, this.requestHeaders());

    fetchBuildings = () => axios.get(`${Config.getInstance.apiUrl}/api/buildings/config`, this.requestHeaders());

    fetchDailyJobs = () => axios.get(`${Config.getInstance.apiUrl}/api/daily_jobs`, this.requestHeaders());

    fetchAchievements = () => axios.get(`${Config.getInstance.apiUrl}/api/achievements`, this.requestHeaders());

    fetchMainJobs = () => axios.get(`${Config.getInstance.apiUrl}/api/main_jobs`, this.requestHeaders());

    fetchProductList = () => axios.get(`${Config.getInstance.apiUrl}/api/product_list`, this.requestHeaders());

    fetchLeagueConfig = () => axios.get(`${Config.getInstance.apiUrl}/api/leagues`, this.requestHeaders());

    fetchConfig = async () => {
        try {
            await Config.getInstance.fetchConfiguration();
        } catch (e) {
            console.error("Failed to fetch config", e);
        }

        const requests = [
            this.fetchShopPackages(),
            this.fetchItems(),
            this.fetchBuildings(),
            this.fetchLocalization(),
            this.fetchDailyJobs(),
            this.fetchAchievements(),
            this.fetchMainJobs(),
            this.fetchProductList(),
            this.fetchLeagueConfig(),
            axios.get(`${Config.getInstance.apiUrl}/api/bounties`, this.requestHeaders()),
            axios.get(`${Config.getInstance.apiUrl}/api/benefits`, this.requestHeaders()),
        ];

        return axios.all(requests).then(axios.spread((
            shop_packages,
            items,
            buildings,
            localization,
            daily_jobs,
            achievements,
            main_jobs,
            product_list,
            leagues,
            bounties,
            benefits
        ) => {
            setGlobalVar("SHOP_PACKAGES", shop_packages.data.data);
            setGlobalVar("ITEMS", items.data.data.sort((a, b) => b.id - a.id));
            setGlobalVar("BUILDINGS", buildings.data.data);
            setGlobalVar("LOCALIZATION", localization.data.data);
            setGlobalVar("DAILY_JOBS", daily_jobs.data.data);
            setGlobalVar("ACHIEVEMENTS", achievements.data.data.achievements);
            setGlobalVar("MAIN_JOBS", main_jobs.data.data);
            setGlobalVar("PRODUCT_LIST", product_list.data.data.items);
            setGlobalVar("LEAGUES", leagues.data.data.items);
            setGlobalVar("BOUNTIES", bounties.data.data.packs);
            setGlobalVar("ALLIANCE_BENEFITS", benefits.data.data.benefits);

            return this.setState({ configLoading: false });
        })).catch(_error => {
            this.setState({ configLoading: false, error: true });
        });
    }

    componentDidCatch(error, info) {
        this.setState({
            error: {
                error,
                stack: info
            },
        });
    }

    checkSession = (checkHeartbeat = false) => {
        const sessionToken = cookie.load("brl-session-token");

        if (!sessionToken) {
            if (checkHeartbeat && !this.state.initHeartbeat || this.state.showLoginPopup) {
                return;
            }

            if (this.state.isAuthorized) {
                this.setState({ showLoginPopup: true });
            } else {
                this.setState({ isAuthorized: false });
            }
        }
    }

    componentDidMount() {
        this.fetchConfig().then(() => {
            this.faviconUpdate();
        });

        // Check if user have already logged in by checking session cookie
        // If cookie exist send it to backend to check if it's valid
        // If it's valid set use as authorized and skip login form
        heartBeat(cookie.load("brl-session-token")).then((response) => {
            this.setState({
                isAuthorized: response.success,
                initHeartbeat: true,
                activeUser: null,
                ...this.constHeartBeatUser(response),
            }, () => {
                // Check if cookie exist every second
                this.sessionCheckTimer = setInterval(() => {
                    this.checkSession();
                }, 1000);
            });
        });
    }

    constHeartBeatUser(response) {
        if (!response.success) {
            return {
                activeUser: null,
                mode: "dark"
            }
        }

        const activeUser = new SessionUser(response.data);

        return  {
            activeUser,
            mode: activeUser.theme,
        }
    }

    componentWillUnmount() {
        clearInterval(this.sessionCheckTimer)
    }

    /**
     * Hook to check if user has been authorized
     * This will be passed to children component
     */
    isAuthorizedCallback = (data: any) => {
        Helpers.pushURL("");

        const activeUser = new SessionUser(data);

        this.setState({
            isAuthorized: true,
            showLoginPopup: false,
            activeUser,
            mode: activeUser.theme,
        });
    }

    resetAppState = () => {
        this.setState({
            isAuthorized: false,
            activeUser: null,
        });
    }

    switchMode = (theme: "dark" | "light" = null) => {
        this.setState({
            mode: theme ? theme : this.state.mode === "dark" ? "light" : "dark"
        })
    }

    render() {
        if (this.state.configLoading) {
            return <LoadingIndicator loadingTextPlaceholder="Fetching configuration" loadingText={true}/>;
        }

        if (this.state.error) {
            return <FatalError error={this.state.error}/>;
        }

        if (!this.state.initHeartbeat) {
            // Check if we checked initial heartbeat
             // This is used to know which page should be shown as first
            return null;
        }

        if (!this.state.isAuthorized) {
            // Redirect to login page
            if (!Helpers.onLoginPage()) {
                Helpers.redirectLoginPage(window.location.pathname);
            }

            return <Authorization isAuthorized={this.isAuthorizedCallback}/>;
        }

        // If everything is alright with all conditions then load page
        return <ThemeProvider theme={createTheme({
            palette: {
                mode: this.state.mode as any,
                primary: Config.getInstance.getSettingByKey("PRIMARY_COLOR"),
                secondary: Config.getInstance.getSettingByKey("SECONDARY_COLOR"),
                neutral: this.state.mode === "dark" ? {
                    main: "#686868",
                    contrastText: "#fff",
                } : {
                    main: "#fff",
                    contrastText: "#fff",
                }
            } as any,
        })}>
            <GlobalStyles styles={this.state.mode === "dark" ? DarkMode : LightMode }/>

            <LayoutWrapper
                activeUser={this.state.activeUser}
                resetAppState={this.resetAppState}
                switchMode={this.switchMode}
                mode={this.state.mode}
            />

            {this.state.showLoginPopup && <Authorization isAuthorized={this.isAuthorizedCallback} modal={true}/>}
        </ThemeProvider>;
    }
}
